import { format, parseISO } from 'date-fns'
import { th } from 'date-fns/locale'
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Skeleton } from '@/common/ui/skeleton'
import useQueryGetAdminPartnerInvitationSummary from '@/modules/manage-player/api/hooks/useQueryGetAdminPartnerInvitationSummary'

interface AdminPartnerInvitationChartProps {
	partnerId: string
	startDate?: string
	endDate?: string
}

const formatNumber = (value: number) =>
	value.toLocaleString('th-TH', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

export default function AdminPartnerInvitationChart({
	partnerId,
	startDate,
	endDate,
}: AdminPartnerInvitationChartProps) {
	const payload = startDate && endDate ? { startDate, endDate } : { days: 7 }

	const { data, isLoading } = useQueryGetAdminPartnerInvitationSummary(
		partnerId,
		payload,
	)

	const chartData = (data?.dailySignups ?? []).map((item) => ({
		date: format(parseISO(item.date), 'd MMM', { locale: th }),
		count: item.count,
	}))

	const summary = data?.summary
	const titleSuffix =
		startDate && endDate ? `${startDate} - ${endDate}` : '7 วันล่าสุด'

	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-base font-medium">
					การแนะนำเพื่อน ({titleSuffix})
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4 pt-4">
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
					<div className="rounded-lg border bg-muted/30 p-3">
						<p className="text-xs text-muted-foreground">สมาชิกใหม่</p>
						<p className="text-lg font-bold">
							{isLoading ? (
								<Skeleton className="h-6 w-16" />
							) : (
								`${summary?.totalSignups ?? 0} คน`
							)}
						</p>
					</div>
					<div className="rounded-lg border bg-muted/30 p-3">
						<p className="text-xs text-muted-foreground">ฝากเงินแล้ว</p>
						<p className="text-lg font-bold">
							{isLoading ? (
								<Skeleton className="h-6 w-16" />
							) : (
								`${summary?.totalDepositedPlayers ?? 0} คน`
							)}
						</p>
					</div>
					<div className="rounded-lg border bg-muted/30 p-3">
						<p className="text-xs text-muted-foreground">ยอดฝากรวม</p>
						<p className="text-lg font-bold text-green-600">
							{isLoading ? (
								<Skeleton className="h-6 w-20" />
							) : (
								formatNumber(summary?.totalDepositAmount ?? 0)
							)}
						</p>
					</div>
					<div className="rounded-lg border bg-muted/30 p-3">
						<p className="text-xs text-muted-foreground">โบนัสรวม</p>
						<p className="text-lg font-bold text-blue-600">
							{isLoading ? (
								<Skeleton className="h-6 w-20" />
							) : (
								formatNumber(summary?.totalDepositBonusAmount ?? 0)
							)}
						</p>
					</div>
				</div>

				{isLoading ? (
					<div className="flex h-[200px] items-center justify-center">
						<Skeleton className="h-full w-full" />
					</div>
				) : chartData.length === 0 ? (
					<div className="flex h-[200px] items-center justify-center text-muted-foreground">
						ไม่มีข้อมูล
					</div>
				) : (
					<ResponsiveContainer width="100%" height={200}>
						<LineChart
							data={chartData}
							margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
						>
							<CartesianGrid
								strokeDasharray="3 3"
								stroke="hsl(var(--border))"
							/>
							<XAxis dataKey="date" tick={{ fontSize: 12 }} />
							<YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
							<Tooltip
								formatter={(value: number) => [`${value} คน`, 'สมาชิกใหม่']}
								contentStyle={{
									backgroundColor: 'hsl(var(--card))',
									border: '1px solid hsl(var(--border))',
									borderRadius: '8px',
								}}
							/>
							<Line
								type="monotone"
								dataKey="count"
								stroke="hsl(var(--primary))"
								strokeWidth={2}
								dot={{ fill: 'hsl(var(--primary))', r: 4 }}
								activeDot={{ r: 6 }}
							/>
						</LineChart>
					</ResponsiveContainer>
				)}
			</CardContent>
		</Card>
	)
}
